import { CheckIcon, MinusIcon } from "lucide-react";
import { useEffect, useRef, type ChangeEvent, type ReactNode } from "react";

import { cn } from "./utils";

const Checkbox = ({
  id = "",
  label = "",
  checked = false,
  indeterminate = false,
  onChange = () => undefined,
  onClick = () => undefined,
  disabled = false,
  required = false,
  error = false,
  className = "",
  type = "checkbox",
}: {
  id?: string;
  label?: string | ReactNode;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  className?: string;
  type?: "checkbox" | "radio";
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    onClick();
  };
  const stateClassName = (() => {
    if (!checked && !indeterminate) {
      return "bg-white text-transparent";
    }

    if (error) {
      return "bg-red-500";
    }

    return "border-blue-600 bg-blue-600";
  })();

  let indicator = (
    <span
      className={cn(
        "h-2 w-2 rounded-full bg-white transition-opacity",
        checked ? "opacity-100" : "opacity-0",
      )}
    />
  );

  if (type === "checkbox") {
    indicator = indeterminate ? (
      <MinusIcon className="h-3 w-3" strokeWidth={3} />
    ) : (
      <CheckIcon className="h-3 w-3" strokeWidth={3} />
    );
  }

  return (
    <div className={cn("flex items-center gap-2 space-y-0", className)}>
      <label
        className={cn(
          "flex items-center gap-2",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        )}
      >
        <input
          ref={inputRef}
          type={type}
          id={id}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          aria-checked={indeterminate ? "mixed" : checked}
          aria-invalid={error}
          className="peer sr-only"
        />
        <span
          aria-hidden
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center border bg-white text-white shadow-sm transition-colors",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white",
            type === "checkbox" ? "rounded-[4px]" : "rounded-full",
            error
              ? "border-red-500 peer-focus-visible:ring-red-500/40"
              : "border-gray-300 peer-focus-visible:ring-blue-500/40",
            stateClassName,
          )}
        >
          {indicator}
        </span>
        {label && (
          <span className="select-none text-xs leading-none text-gray-900">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </span>
        )}
      </label>
    </div>
  );
};

export default Checkbox;
